import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import QRCode from 'react-qr-code';
import apiClient, { get, post } from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import TelefonosRegistrados from './TelefonosWhatsAppPage.jsx';

/**
 * Panel de administración de WhatsApp — puerto de
 * `IbuFront/src/components/views/WhatsApp/Whatsapp.jsx` (V1) contra los endpoints
 * REALES de `IbuBack2/src/api/v1/modules/whatsapp/whatsapp.routes.js`:
 *   GET  /whatsapp/status      -> { connected, state, message }
 *   GET  /whatsapp/login       -> { ready, qr?, message } (el backend YA espera hasta
 *                                  ~30s a que aparezca el QR o la sesión conecte, ver
 *                                  `whatsapp.service.js#login` — no hace falta long-poll
 *                                  del lado del cliente para ESTA llamada)
 *   POST /whatsapp/send-msg    { number, message } -> RESPUESTA SIN envolver en
 *                                  `{success,data}` (bypassa `res.success`, ver
 *                                  `whatsapp.controller.js#sendMsg`): responde
 *                                  `{error,message,waId?}` con status 200/400 directo.
 *   POST /whatsapp/restart     -> reinicia el socket sin perder la sesión
 *   POST /whatsapp/hard-reset  -> borra credenciales y fuerza un nuevo QR
 *
 * DUDAS.md D-11.1: `whatsapp.routes.js` gatea TODO el router (incluido /status y
 * /login, no solo /hard-reset) con `requireAdmin` — no hay endpoint de lectura para
 * "iaWhatsapp" no-admin como sugiere la tarea. Como el backend rechazaría cualquier
 * llamada de un no-admin con 403, esta página completa se oculta detrás de
 * `isAdmin()` (no solo el botón de "Reinicio completo") en vez de dejar ver un panel
 * que fallaría en cada acción.
 *
 * Polling: cada 5s se refresca el estado de conexión en segundo plano. Mientras hay
 * un QR pendiente de escaneo se acelera a cada 2.5s (para ocultar el QR en cuanto el
 * usuario lo escanea) — ver `useEffect` de abajo.
 */
export default function WhatsAppPanelPage() {
  const { isAdmin } = useContext(AuthContext);
  const puedeAdministrar = isAdmin();

  const [status, setStatus] = useState(null);
  const [cargandoStatus, setCargandoStatus] = useState(true);
  const [qr, setQr] = useState(null);
  const [cargandoLogin, setCargandoLogin] = useState(false);
  const [reiniciando, setReiniciando] = useState(false);
  const [reseteando, setReseteando] = useState(false);

  const [number, setNumber] = useState('');
  const [message, setMessage] = useState('');
  const [enviando, setEnviando] = useState(false);

  const intervalRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await get('/whatsapp/status');
      setStatus(data);
      if (data?.connected) setQr(null); // ya se escaneó / ya está conectado, oculta el QR
      return data;
    } catch (e) {
      setStatus({ connected: false, state: 'Error', message: e.message });
      return null;
    } finally {
      setCargandoStatus(false);
    }
  }, []);

  // Polling de estado: 5s normalmente, 2.5s mientras hay un QR esperando escaneo.
  useEffect(() => {
    if (!puedeAdministrar) return undefined;
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, qr ? 2500 : 5000);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeAdministrar, fetchStatus, !!qr]);

  async function iniciarSesion() {
    setCargandoLogin(true);
    try {
      const data = await get('/whatsapp/login');
      if (data.ready) {
        setQr(null);
        Swal.fire({ icon: 'success', title: data.message || 'Ya está conectado', timer: 2000, showConfirmButton: false });
      } else {
        setQr(data.qr);
        Swal.fire({ icon: 'info', title: data.message || 'Escanea el código QR con WhatsApp', timer: 2500, showConfirmButton: false });
      }
      await fetchStatus();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo generar el QR', text: e.message });
    } finally {
      setCargandoLogin(false);
    }
  }

  async function reiniciar() {
    const r = await Swal.fire({
      title: '¿Reiniciar la sesión de WhatsApp?', text: 'Reinicia el socket sin borrar la sesión guardada.',
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Sí, reiniciar', confirmButtonColor: '#3085d6',
    });
    if (!r.isConfirmed) return;
    setReiniciando(true);
    try {
      await post('/whatsapp/restart', {});
      Swal.fire({ icon: 'success', title: 'Reinicio solicitado', timer: 2000, showConfirmButton: false });
      await fetchStatus();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo reiniciar', text: e.message });
    } finally {
      setReiniciando(false);
    }
  }

  async function resetCompleto() {
    const r = await Swal.fire({
      title: '¿Reinicio completo?', icon: 'warning',
      text: 'Borra las credenciales guardadas: se deberá escanear un QR nuevo desde cero. Esta acción no se puede deshacer.',
      showCancelButton: true, confirmButtonText: 'Sí, borrar todo', confirmButtonColor: '#d33',
    });
    if (!r.isConfirmed) return;
    setReseteando(true);
    try {
      await post('/whatsapp/hard-reset', {});
      setQr(null);
      Swal.fire({ icon: 'success', title: 'Sesión reiniciada por completo', timer: 2000, showConfirmButton: false });
      await fetchStatus();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo reiniciar por completo', text: e.message });
    } finally {
      setReseteando(false);
    }
  }

  async function enviarMensajePrueba(e) {
    e.preventDefault();
    if (!number.trim() || !message.trim()) {
      Swal.fire({ icon: 'warning', title: 'Captura número y mensaje' });
      return;
    }
    setEnviando(true);
    try {
      // `send-msg` responde SIN envolver en {success,data} (ver nota del módulo) --
      // no se puede usar el helper `post()` (espera `r.data.data`), se llama
      // `apiClient` directo y se lee el cuerpo crudo.
      const { data } = await apiClient.post('/whatsapp/send-msg', { number, message });
      if (data.error) {
        Swal.fire({ icon: 'error', title: 'No se pudo enviar', text: data.message });
      } else {
        Swal.fire({ icon: 'success', title: data.message || 'Enviado', timer: 2000, showConfirmButton: false });
        setNumber('');
        setMessage('');
      }
    } catch (err) {
      // 400 del backend: el cuerpo crudo `{error:true,message}` viene en err.response.data
      // (el interceptor genérico de apiClient no lo desempaqueta porque no trae la forma
      // `{success,error:{code,message}}` esperada).
      const msg = err.response?.data?.message || err.message;
      Swal.fire({ icon: 'error', title: 'No se pudo enviar', text: msg });
    } finally {
      setEnviando(false);
    }
  }

  if (!puedeAdministrar) {
    return (
      <div className="rounded-md border border-warning-200 bg-warning-50 text-warning-800 text-sm px-4 py-3">
        Este módulo requiere permisos de administrador (el backend gatea TODAS las rutas de
        WhatsApp, incluida la consulta de estado, con <code>requireAdmin</code>).
      </div>
    );
  }

  const conectado = !!status?.connected;

  // Réplica de V1 (`Whatsapp.jsx`): una sola pantalla "WhatsApp" -- columna izquierda con
  // estado + "Enviar Mensaje de Prueba" + "Generar QR"; columna derecha "Telefonos
  // Registrados". "Reiniciar"/"Reinicio completo" no existen en V1 pero los pidió el
  // brief original del módulo (ver DUDAS.md D-11.13) y se conservan.
  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-900 text-center">WhatsApp</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <span className={conectado ? 'badge-success' : 'badge-danger'}>
            {cargandoStatus ? 'Esperando Estado...' : status?.state || 'Sin datos'}
          </span>

          <h2 className="text-lg font-semibold text-slate-900">Enviar Mensaje de Prueba</h2>
          <form onSubmit={enviarMensajePrueba} className="space-y-3">
            <div>
              <label className="label">Número de Teléfono:</label>
              <input
                type="number" value={number} onChange={(e) => setNumber(e.target.value)}
                className="input-base w-full"
              />
            </div>
            <div>
              <label className="label">Mensaje:</label>
              <input
                type="text" value={message} onChange={(e) => setMessage(e.target.value)}
                className="input-base w-full"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="submit" disabled={enviando} className="btn-primary disabled:opacity-60">
                {enviando ? 'Enviando...' : 'Enviar Mensaje'}
              </button>
              <button type="button" onClick={iniciarSesion} disabled={cargandoLogin} className="btn-outline disabled:opacity-60">
                {cargandoLogin ? 'Generando QR...' : 'Generar QR'}
              </button>
              <button type="button" onClick={reiniciar} disabled={reiniciando} className="btn-outline disabled:opacity-60">
                {reiniciando ? 'Reiniciando...' : 'Reiniciar'}
              </button>
              <button type="button" onClick={resetCompleto} disabled={reseteando} className="btn-danger disabled:opacity-60">
                {reseteando ? 'Reiniciando por completo...' : 'Reinicio completo'}
              </button>
            </div>
          </form>

          {qr && (
            <div className="inline-block rounded-md border border-slate-200 p-3 bg-white">
              <QRCode value={qr} size={200} />
            </div>
          )}
        </div>

        <TelefonosRegistrados />
      </div>
    </div>
  );
}
